import {
  DocumentReference,
  INamedDocument,
  isDocRef,
  isEnumValue,
  isINamedDocument,
  isObject,
  ISoftDelete,
  isTimestamp,
  Timestamp,
  TypeGuard,
} from '@principle-theorem/shared';
import { isBoolean, isString } from 'lodash';
import { IPatient } from '../../patient/patient';
import { IStaffer } from '../../staffer/staffer';
import {
  IAccClaimFundingRequest,
  IAccClaimFundingRequestData,
} from './acc/acc-claim';
import {
  IAccInvoiceFundingRequest,
  IAccInvoiceFundingRequestData,
} from './acc/acc-invoice';
import {
  IMohClaimFundingRequest,
  IMohClaimFundingRequestData,
} from './moh/moh-claim';
import {
  IMohPreApprovalFundingRequest,
  IMohPreApprovalFundingRequestData,
} from './moh/moh-pre-approval';

export enum FundingRequestCollection {
  Attachments = 'attachments',
}

export enum FundingRequestType {
  Claim = 'claim',
  PreApproval = 'preApproval',
}

export enum FundingRequestStatus {
  Approved = 'approved',
  Cancelled = 'cancelled',
  Declined = 'declined',
  Draft = 'draft',
  Failed = 'failed',
  Paid = 'paid',
  PartiallyPaid = 'partiallyPaid',
  Pending = 'pending',
  Redeemed = 'redeemed',
  Submitted = 'submitted',
}

export const FUNDING_REQUEST_STATUS_COLOUR_MAP: {
  [key in FundingRequestStatus]: string;
} = {
  [FundingRequestStatus.Approved]: 'success',
  [FundingRequestStatus.Cancelled]: 'warn',
  [FundingRequestStatus.Declined]: 'warn',
  [FundingRequestStatus.Draft]: 'default',
  [FundingRequestStatus.Failed]: 'warn',
  [FundingRequestStatus.Paid]: 'success',
  [FundingRequestStatus.PartiallyPaid]: 'success',
  [FundingRequestStatus.Pending]: 'default',
  [FundingRequestStatus.Redeemed]: 'success',
  [FundingRequestStatus.Submitted]: 'primary',
};

export enum FundingRequestProvider {
  MOH = 'moh',
  ACC = 'acc',
}

export interface IFundingRequest<TProviderData> extends ISoftDelete {
  type: FundingRequestType;
  fundingProvider: FundingRequestProvider;
  status: FundingRequestStatus;
  data: TProviderData;
  patient: INamedDocument<IPatient>;
  practitioner?: INamedDocument<IStaffer>;
  errorMessage?: string;
  cloudTaskId?: string;
  syncedAt?: Timestamp;
}

export const isFundingRequest = TypeGuard.interface<IFundingRequest<unknown>>({
  type: TypeGuard.enumValue(FundingRequestType),
  fundingProvider: TypeGuard.enumValue(FundingRequestProvider),
  status: TypeGuard.enumValue(FundingRequestStatus),
  data: isObject,
  patient: isINamedDocument,
  practitioner: TypeGuard.undefinedOr(isINamedDocument),
  errorMessage: TypeGuard.undefinedOr(isString),
  cloudTaskId: TypeGuard.undefinedOr(isString),
  syncedAt: TypeGuard.undefinedOr(isTimestamp),
  deleted: isBoolean,
});

export type AnyFundingRequest = IFundingRequest<
  | IMohClaimFundingRequestData
  | IMohPreApprovalFundingRequestData
  | IAccClaimFundingRequestData
  | IAccInvoiceFundingRequestData
>;

export type MohFundingRequest =
  | IMohClaimFundingRequest
  | IMohPreApprovalFundingRequest;

export interface IFundingRequestSyncTask extends ISoftDelete {
  cloudTaskId: string;
  status: unknown;
  requestRef: DocumentReference<AnyFundingRequest>;
}

export enum FundingRequestSyncTaskType {
  AccSyncInvoice = 'accSyncInvoice',
  AccSyncClaim = 'accSyncClaim',
}

export type FundingRequestSyncTaskTypeMap = {
  [FundingRequestSyncTaskType.AccSyncInvoice]: IAccInvoiceFundingRequest;
  [FundingRequestSyncTaskType.AccSyncClaim]: IAccClaimFundingRequest;
};

export type IFundingRequestSyncTaskPayload = {
  [T in FundingRequestSyncTaskType]: {
    fundingRequestRef: DocumentReference<FundingRequestSyncTaskTypeMap[T]>;
    type: T;
    testMode: boolean;
  };
}[FundingRequestSyncTaskType];

export const isFundingRequestSyncTaskPayload = (
  value: unknown
): value is IFundingRequestSyncTaskPayload =>
  isObject(value) &&
  'fundingRequestRef' in value &&
  isDocRef(value.fundingRequestRef) &&
  'type' in value &&
  isEnumValue(FundingRequestSyncTaskType, value.type) &&
  'testMode' in value &&
  isBoolean(value.testMode);
